/**
 * Tiny helper around the RBAC context the Signin page wrote to localStorage.
 *
 *   const { isSuperAdmin, can, currentUserId, permissions } = useRbac();
 *   if (can('employees.delete')) { ... }
 *
 * Returns plain primitives — no React state. Components that need to react to
 * permission changes should re-render on login/logout (which already reloads
 * the app), so plain reads are fine.
 */
const safeParse = (raw, fallback) => {
  try {
    if (raw === null || raw === undefined) return fallback;
    return JSON.parse(raw);
  } catch (_e) {
    return fallback;
  }
};

const useRbac = () => {
  const explicitSuper = safeParse(localStorage.getItem("is_super_admin"), null);
  const isSeedAdmin = safeParse(localStorage.getItem("is_seed_admin"), false) === true;
  const roleCode = safeParse(localStorage.getItem("role_code"), null);
  const roleName = safeParse(localStorage.getItem("role_name"), null);
  const permissionsRaw = safeParse(localStorage.getItem("permissions"), null);
  const userData = safeParse(localStorage.getItem("UserData"), {});
  const currentUserId = userData?._id || null;

  // Legacy fallback: an old session from before RBAC won't have the new
  // localStorage keys. If the user's role_name was "admin" (the original
  // single-admin role) OR "super_admin", treat them as super-admin in the UI
  // so the new pages aren't completely empty for them. They should log out
  // and back in to get proper RBAC state.
  const hasExplicitRbac = explicitSuper !== null || Array.isArray(permissionsRaw);
  const legacySuperAdmin = !hasExplicitRbac && (roleName === "admin" || roleName === "super_admin");

  const isSuperAdmin = explicitSuper === true || legacySuperAdmin;
  const permissions = Array.isArray(permissionsRaw) ? permissionsRaw : [];

  const can = (code) => {
    if (isSuperAdmin) return true;
    return permissions.includes(code);
  };

  const canAny = (codes = []) => {
    if (isSuperAdmin) return true;
    return codes.some((c) => permissions.includes(c));
  };

  return {
    isSuperAdmin,
    isSeedAdmin,
    roleCode,
    roleName,
    permissions,
    currentUserId,
    can,
    canAny,
    /** True when the data came from a fresh post-RBAC login. False = legacy session. */
    hasFreshRbacSession: hasExplicitRbac,
  };
};

export default useRbac;
